import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/capacitor";
import * as SentryReact from "@sentry/react";
import { Preferences } from "@capacitor/preferences";
import { isNative } from "./lib/platform";
import { initializeSocialAuth } from "./supabase/socialAuth";
import App from "./App";
import { ErrorBoundary } from "./ErrorBoundary";
import "./index.css";

Sentry.init(
  {
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: import.meta.env.VITE_APP_VERSION ?? "1.0.0",
    environment: import.meta.env.DEV ? "development" : "production",
    enabled: !import.meta.env.DEV,
    tracesSampleRate: 0.1,
  },
  SentryReact.init,
);

// ── Native storage bridge ─────────────────────────────────────────────────
// Wire @capacitor/preferences into the window.storage interface that
// src/data/storage.ts already knows how to use (createWindowStorageAdapter).
// Must run before React renders so all getStorage() calls see it.
if (isNative) {
  (
    window as Window & {
      storage?: {
        get: (k: string) => Promise<{ value: string | null }>;
        set: (k: string, v: string | null) => Promise<void>;
        delete: (k: string) => Promise<void>;
      };
    }
  ).storage = {
    get: (key) => Preferences.get({ key }),
    set: (key, value) =>
      value === null ? Preferences.remove({ key }) : Preferences.set({ key, value }),
    delete: (key) => Preferences.remove({ key }),
  };

  // Restore the iOS prev/next/done accessory bar above the keyboard. Capacitor
  // hides it by default on iPhones; users expect the native iOS form-input feel.
  void (async () => {
    try {
      const { Keyboard } = await import("@capacitor/keyboard");
      await Keyboard.setAccessoryBarVisible({ isVisible: true });
    } catch (err) {
      console.warn("[keyboard] setAccessoryBarVisible failed", err);
    }
  })();
}

void initializeSocialAuth();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);
